// src/onboarding/ui/PersonalizationBanner.jsx
//
// The slim bar under the header (decision 8). Permanent while the workspace is not personalized
// (a generic workspace writes worse content); an in-progress line while a run is going (content
// generated now still uses the previous identity — the backend does not block generation); a
// dismissable notice when a run the user was waiting for ended — the ONLY dismissable mode.
// On the pages that carry the badge (onboardingModel.hasBadge) the standing lines are not shown —
// they would only repeat it — but the outcome notice still is: the badge cannot say "the last
// attempt did not finish" or "the start was not accepted". ⛔ No cost copy.
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useOnboarding, dismissOnboardingOutcome } from '../onboardingStore.js';
import { bannerMode, canRunAgain, PERSONALIZATION_PATH } from '../onboardingModel.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const OUTCOME_TONE = {
  completed: SEMANTIC.verde,
  failed: SEMANTIC.attenzione,
  rerunFailed: SEMANTIC.attenzione,
  startRefused: SEMANTIC.attenzione,
  startUnconfirmed: SEMANTIC.attenzione,
};

const PROMPT_ACTION = { notPersonalized: 'banner.personalize', failed: 'banner.retry', stale: 'banner.restart' };

const bar = (sem) => ({
  display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', flexShrink: 0,
  padding: '10px 40px', background: sem.bg, color: sem.fg,
  borderBottom: `1px solid ${COLORS.border}`, fontFamily: FONT_SANS, fontSize: '13px',
});
const action = (sem) => ({
  padding: '4px 12px', borderRadius: RADII.controlInner, border: `1px solid ${sem.fg}`,
  color: sem.fg, background: 'transparent', fontSize: '12px', fontWeight: 600, textDecoration: 'none',
});
const TEXT = { flex: 1, minWidth: 0 };

export default function PersonalizationBanner() {
  const { t } = useTranslation('onboarding');
  const { activeTenant } = useAuth();
  const snapshot = useOnboarding();
  const { pathname } = useLocation();
  const mode = bannerMode(snapshot, activeTenant?.tenant_id || null, pathname);
  if (!mode) return null;

  if (mode === 'running') {
    const stage = snapshot.status.personalization?.status;
    const stageLabel = t(`stages.${stage}`, { defaultValue: t('stages.unknown') });
    return (
      <div role="status" style={bar(SEMANTIC.info)} data-testid="personalization-banner">
        <span style={TEXT}>{t('banner.running', { stage: stageLabel })}</span>
        <Link to={PERSONALIZATION_PATH} style={action(SEMANTIC.info)}>{t('banner.details')}</Link>
      </div>
    );
  }

  if (mode === 'outcome') {
    const kind = snapshot.outcome.kind;
    const sem = OUTCOME_TONE[kind] || SEMANTIC.info;
    // v192: a refused start sends to the wizard's explanation ("Dettagli"); a failed RE-run offers
    // "Riprova" only where the plan allows a second run (canRunAgain — PLAN-level, second run only).
    let actionKey = null;
    if (kind === 'startRefused') actionKey = 'banner.details';
    else if (kind === 'rerunFailed') actionKey = canRunAgain(snapshot.status) ? 'banner.retry' : null;
    else if (kind !== 'completed') actionKey = 'banner.retry';
    return (
      <div role="status" style={bar(sem)} data-testid="personalization-banner">
        <span style={TEXT}>{t(`banner.outcome.${kind}`)}</span>
        {actionKey && (
          <Link to={PERSONALIZATION_PATH} style={action(sem)}>{t(actionKey)}</Link>
        )}
        <button
          type="button" onClick={dismissOnboardingOutcome} aria-label={t('banner.dismiss')}
          style={{ background: 'none', border: 'none', color: sem.fg, cursor: 'pointer', padding: 0, display: 'flex' }}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    );
  }

  // 'notPersonalized' | 'failed' | 'stale'
  return (
    <div role="status" style={bar(SEMANTIC.attenzione)} data-testid="personalization-banner">
      <span style={TEXT}>{t(`banner.${mode}`)}</span>
      <Link to={PERSONALIZATION_PATH} style={action(SEMANTIC.attenzione)}>{t(PROMPT_ACTION[mode])}</Link>
    </div>
  );
}
